// src/odoo/customers/components/CustomerOutstandingInvoicesDrawer.tsx
// All comments are in English.

import React, { useMemo } from "react";
import type { InvoiceDocument } from "../api/documentsApi";
import { formatHKD } from "./utils";

// Odoo web base url for opening the customer form
const ODOO_WEB_BASE_URL = import.meta.env.VITE_ODOO_WEB_URL;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoices?: InvoiceDocument[];
}

type AgingBucketKey = "0_30" | "31_60" | "61_90" | "90_plus";

interface GroupedInvoices {
  "0_30": InvoiceDocument[];
  "31_60": InvoiceDocument[];
  "61_90": InvoiceDocument[];
  "90_plus": InvoiceDocument[];
}

/**
 * Compute days overdue for an invoice.
 * Prefer backend-computed overdue_days; fallback to local calculation.
 */
function getDaysOverdue(inv: InvoiceDocument, now: Date): number {
  // 1) Prefer backend-computed overdue_days when available
  if (typeof inv.overdue_days === "number" && !Number.isNaN(inv.overdue_days)) {
    return inv.overdue_days < 0 ? 0 : inv.overdue_days;
  }

  // 2) Fallback: compute from due date
  const dueStr = inv.invoice_date_due || inv.due_date || inv.invoice_date;
  if (!dueStr) {
    return 0;
  }

  const dueDate = new Date(dueStr);
  if (Number.isNaN(dueDate.getTime())) {
    return 0;
  }

  const diffMs = now.getTime() - dueDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return diffDays < 0 ? 0 : diffDays;
}

/**
 * Determine which aging bucket the invoice belongs to.
 */
function getAgingBucket(inv: InvoiceDocument, now: Date): AgingBucketKey {
  const daysOverdue = getDaysOverdue(inv, now);

  if (daysOverdue <= 30) return "0_30";
  if (daysOverdue <= 60) return "31_60";
  if (daysOverdue <= 90) return "61_90";
  return "90_plus";
}

/**
 * A right-side drawer listing outstanding invoices grouped by aging bucket.
 * This component is UI-only and does not fetch data by itself.
 */
const CustomerOutstandingInvoicesDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  invoices,
}) => {
  const { groups, totalCount } = useMemo(() => {
    const emptyGroups: GroupedInvoices = {
      "0_30": [],
      "31_60": [],
      "61_90": [],
      "90_plus": [],
    };

    if (!invoices || !invoices.length) {
      return {
        groups: emptyGroups,
        totalCount: 0,
      };
    }

    // Filter only invoices with positive outstanding balance
    const outstanding = invoices.filter((inv) => {
      const residual =
        typeof inv.residual === "number" ? inv.residual : Number(inv.residual ?? 0);
      return residual > 0;
    });

    if (!outstanding.length) {
      return {
        groups: emptyGroups,
        totalCount: 0,
      };
    }

    const now = new Date();
    const grouped: GroupedInvoices = {
      "0_30": [],
      "31_60": [],
      "61_90": [],
      "90_plus": [],
    };

    outstanding.forEach((inv) => {
      const bucket = getAgingBucket(inv, now);
      grouped[bucket].push(inv);
    });

    // Sort each bucket by due date ascending
    (Object.keys(grouped) as AgingBucketKey[]).forEach((bucketKey) => {
      grouped[bucketKey].sort((a, b) => {
        const aDate = new Date(
          a.invoice_date_due || a.due_date || a.invoice_date || 0
        ).getTime();
        const bDate = new Date(
          b.invoice_date_due || b.due_date || b.invoice_date || 0
        ).getTime();
        return aDate - bDate;
      });
    });

    const count =
      grouped["0_30"].length +
      grouped["31_60"].length +
      grouped["61_90"].length +
      grouped["90_plus"].length;

    return {
      groups: grouped,
      totalCount: count,
    };
  }, [invoices]);

  if (!isOpen) return null;

  const hasOutstanding = totalCount > 0;

  const agingBuckets: { key: AgingBucketKey; label: string }[] = [
    { key: "0_30", label: "< 30 DAYS" },
    { key: "31_60", label: "31–60 DAYS" },
    { key: "61_90", label: "61–90 DAYS" },
    { key: "90_plus", label: "> 90 DAYS" },
  ];

  const handleRowClick = (invoiceId: number) => {
    const url = `${ODOO_WEB_BASE_URL}#id=${invoiceId}&cids=2-1-3&menu_id=121&action=253&model=account.move&view_type=form`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30">
      <div className="w-full max-w-xl h-full bg-card shadow-xl border-l flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/40">
          <div>
            <h2 className="text-base font-semibold">Outstanding invoices</h2>
            <p className="text-xs text-muted-foreground">
              Invoices with remaining outstanding balance, grouped by aging
              bucket.
            </p>
          </div>
          <button
            type="button"
            className="text-xs px-2 py-1 border rounded hover:bg-muted transition-colors"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!hasOutstanding ? (
            <p className="text-sm text-muted-foreground">
              No outstanding invoices for this customer.
            </p>
          ) : (
            <div className="space-y-4">
              {agingBuckets.map(({ key, label }) => {
                const bucketInvoices = groups[key];
                if (!bucketInvoices.length) return null;

                return (
                  <section key={key}>
                    <h3 className="text-xs font-semibold text-gray-700 mb-1">
                      {label} ({bucketInvoices.length})
                    </h3>
                    <div className="overflow-hidden rounded border bg-background">
                      <table className="min-w-full text-xs">
                        <thead className="bg-muted/60">
                          <tr>
                            <th className="px-2 py-1 text-left font-semibold">
                              Invoice
                            </th>
                            <th className="px-2 py-1 text-left font-semibold">
                              Dates
                            </th>
                            <th className="px-2 py-1 text-right font-semibold">
                              Outstanding
                            </th>
                            <th className="px-2 py-1 text-left font-semibold">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {bucketInvoices.map((inv) => {
                            const residual =
                              typeof inv.residual === "number"
                                ? inv.residual
                                : Number(inv.residual ?? 0);

                            const dueLabel =
                              inv.invoice_date_due ||
                              inv.due_date ||
                              inv.invoice_date ||
                              "N/A";

                            return (
                              <tr
                                key={inv.invoice_id}
                                className="border-t hover:bg-muted/40 cursor-pointer transition-colors"
                                onClick={() => handleRowClick(inv.invoice_id)}
                              >
                                <td className="px-2 py-1 whitespace-nowrap">
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {inv.invoice_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap text-xs">
                                  <div className="flex flex-col">
                                    <span>
                                      Invoice:{" "}
                                      {inv.invoice_date ?? "N/A"}
                                    </span>
                                    <span>Due: {dueLabel}</span>
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-right whitespace-nowrap">
                                  <span className="font-semibold">
                                    {formatHKD(residual || inv.amount_total || 0)}
                                  </span>
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap text-xs">
                                  {(inv.payment_state ||
                                    inv.state ||
                                    "unknown"
                                  ).toUpperCase()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerOutstandingInvoicesDrawer;
